/** Return a new string truncated at the first null byte.
    The `s` flag is load-bearing: SF2 name fields are fixed-width and the padding after
    the terminator is arbitrary bytes, newlines included — without dotAll, `.` stops at
    a 0x0A and /\u0000.*$/ silently matched NOTHING for those names, leaving the padding
    in place and breaking exact-match lookups. */
function stripNulls(s: string)
  s.replace(/\u0000.*$/s, "")

export type ModGen =
  type: typeof SF2Parser.GeneratorEnumeratorTable[number]
  value:
    amount?: number
    lo?: number
    hi?: number

export type BagModGen = Record<NonNullable<
  typeof SF2Parser['GeneratorEnumeratorTable'][number]>,
  ModGen['value']
> & {
  "unknown": ModGen['value'][]
}

export class SF2Parser
  input: Uint8Array
  name?: string
  //@ts-ignore
  samplingData: Chunk
  //@ts-ignore
  instrumentZone: ReturnType<SF2Parser["parseIbag"]>
  //@ts-ignore
  instrumentZoneModulator: ModGen[]
  //@ts-ignore
  instrumentZoneGenerator: ModGen[]
  //@ts-ignore
  presetHeader: ReturnType<SF2Parser["parsePhdr"]>
  //@ts-ignore
  presetZone: ReturnType<SF2Parser["parsePbag"]>
  //@ts-ignore
  presetZoneModulator: ModGen[]
  //@ts-ignore
  presetZoneGenerator: ModGen[]
  //@ts-ignore
  instrument: ReturnType<SF2Parser["parseInst"]>
  //@ts-ignore
  sampleHeader: ReturnType<SF2Parser["parseShdr"]>["sampleHeader"]
  //@ts-ignore
  samples: ReturnType<SF2Parser["parseShdr"]>["samples"]

  constructor(@input: Uint8Array)

  parse(): void
    parser := new RiffParser @input
    parser.parse()

    if parser.chunkList.length !== 1
      /* c8 ignore next */
      throw new Error("wrong chunk length")

    chunk := parser.getChunk(0)
    unless chunk?
      /* c8 ignore next */
      throw new Error("chunk not found")

    @parseRiffChunk(chunk)

  parseRiffChunk(chunk: Chunk)
    data := @input
    ip .= chunk.offset

    chunk.assertType "RIFF"

    // check signature
    signature := String.fromCharCode(
      data[ip++],
      data[ip++],
      data[ip++],
      data[ip++]
    )
    if signature !== "sfbk"
      /* c8 ignore next */
      throw new Error("invalid signature:" + signature)

    // read structure
    parser := new RiffParser data,
      index: ip
      length: chunk.size - 4

    parser.parse()
    if parser.getNumberOfChunks() !== 3
      /* c8 ignore next */
      throw new Error "invalid sfbk structure"

    @parseInfoList parser.getChunk(0)!
    @parseSdtaList parser.getChunk(1)!
    @parsePdtaList parser.getChunk(2)!

  parseInfoList(chunk: Chunk)
    data := @input
    ip .= chunk.offset

    chunk.assertType "LIST"

    // check signature
    signature := String.fromCharCode(
      data[ip++],
      data[ip++],
      data[ip++],
      data[ip++]
    )
    if signature !== "INFO"
      /* c8 ignore next */
      throw new Error("invalid signature:" + signature)

    // read structure
    parser := new RiffParser(data, { index: ip, length: chunk.size - 4 })
    parser.parse()

    // Grabbing the name out of the info chunk
    infoChunks := parser.chunkList
    infoChunks.filter (c) =>
      if c.type.match(/^inam$/i)
        @name = stripNulls(
          String.fromCharCode.apply(
            null,
            //@ts-ignore
            data.slice(c.offset, c.offset + c.size)
          )
        )

  parseSdtaList(chunk: Chunk)
    data := @input
    ip .= chunk.offset

    chunk.assertType "LIST"

    // check signature
    signature := String.fromCharCode(
      data[ip++],
      data[ip++],
      data[ip++],
      data[ip++]
    )
    if signature !== "sdta"
      /* c8 ignore next */
      throw new Error("invalid signature:" + signature)

    parser := new RiffParser(data, { index: ip, length: chunk.size - 4 })
    parser.parse()
    // SF2 2.04 may put an `sm24` chunk beside `smpl`: the low byte of each 24-bit sample,
    // with `smpl` still holding the upper 16 bits. The spec has 16-bit readers ignore it
    // and play the font at 16-bit resolution, which is what this synth does — so pick
    // `smpl` and skip the rest, rather than refusing the whole font.
    smpl := parser.chunkList.find (c) -> c.type is "smpl"
    unless smpl
      /* c8 ignore next */
      throw new Error("sdta list has no smpl chunk")

    @samplingData = smpl

  parsePdtaList(chunk: Chunk)
    data := @input
    ip .= chunk.offset

    // check parse target
    chunk.assertType "LIST"

    // check signature
    signature := String.fromCharCode(
      data[ip++],
      data[ip++],
      data[ip++],
      data[ip++]
    )
    if signature !== "pdta"
      /* c8 ignore next */
      throw new Error("invalid signature:" + signature)

    // read structure
    parser := new RiffParser(data, { index: ip, length: chunk.size - 4 })
    parser.parse()

    // check number of chunks
    if parser.getNumberOfChunks() !== 9
      /* c8 ignore next */
      throw new Error("invalid pdta chunk")

    @presetHeader = @parsePhdr parser.getChunk(0)!
    @presetZone = @parsePbag parser.getChunk(1)!
    @presetZoneModulator = @parsePmod parser.getChunk(2)!
    @presetZoneGenerator = @parsePgen parser.getChunk(3)!
    @instrument = @parseInst parser.getChunk(4)!
    @instrumentZone = @parseIbag parser.getChunk(5)!
    @instrumentZoneModulator = @parseImod parser.getChunk(6)!
    @instrumentZoneGenerator = @parseIgen parser.getChunk(7)!
    { @sampleHeader, @samples } = @parseShdr parser.getChunk(8)!

  parsePhdr(chunk: Chunk)
    data := @input
    ip .= chunk.offset
    size := chunk.offset + chunk.size

    chunk.assertType "phdr"

    while ip < size
      presetName: stripNulls
        String.fromCharCode.apply
          null
          //@ts-ignore
          data.subarray(ip, (ip += 20))
      preset: data[ip++] | (data[ip++] << 8),
      bank: data[ip++] | (data[ip++] << 8),
      presetBagIndex: data[ip++] | (data[ip++] << 8),
      library:
        (data[ip++] |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0,
      genre:
        (data[ip++] |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0,
      morphology:
        (data[ip++] |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0,

  parsePbag(chunk: Chunk)
    data := @input
    ip .= chunk.offset
    size := chunk.offset + chunk.size

    chunk.assertType "pbag"

    while ip < size
      presetGeneratorIndex: data[ip++] | (data[ip++] << 8),
      presetModulatorIndex: data[ip++] | (data[ip++] << 8),

  parsePmod(chunk: Chunk)
    chunk.assertType "pmod"

    @parseModulator(chunk)

  parsePgen(chunk: Chunk)
    chunk.assertType "pgen"

    @parseGenerator(chunk)

  parseInst(chunk: Chunk)
    data := @input
    ip .= chunk.offset
    size := chunk.offset + chunk.size

    chunk.assertType "inst"

    while ip < size
      instrumentName: stripNulls String.fromCharCode.apply
        null,
        //@ts-ignore
        data.subarray(ip, (ip += 20))

      instrumentBagIndex: data[ip++] | (data[ip++] << 8)

  parseIbag(chunk: Chunk)
    data := @input
    ip .= chunk.offset
    size := chunk.offset + chunk.size

    chunk.assertType "ibag"

    while (ip < size)
      instrumentGeneratorIndex: data[ip++] | (data[ip++] << 8),
      instrumentModulatorIndex: data[ip++] | (data[ip++] << 8),

  parseImod(chunk: Chunk)
    chunk.assertType "imod"

    @parseModulator(chunk)

  parseIgen(chunk: Chunk)
    chunk.assertType "igen"

    @parseGenerator(chunk)

  parseShdr(chunk: Chunk)
    data := @input
    ip .= chunk.offset
    samples := []
    sampleHeader := []
    size := chunk.offset + chunk.size

    chunk.assertType "shdr"

    while ip < size
      sampleName := stripNulls String.fromCharCode.apply
        null
        //@ts-ignore
        data.subarray(ip, (ip += 20))
      start :=
        ((data[ip++] << 0) |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0
      end :=
        ((data[ip++] << 0) |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0
      startLoop .=
        ((data[ip++] << 0) |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0
      endLoop .=
        ((data[ip++] << 0) |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0
      sampleRate :=
        ((data[ip++] << 0) |
          (data[ip++] << 8) |
          (data[ip++] << 16) |
          (data[ip++] << 24)) >>>
        0
      originalPitch := data[ip++]
      pitchCorrection := (data[ip++] << 24) >> 24
      sampleLink := data[ip++] | (data[ip++] << 8)
      sampleType := data[ip++] | (data[ip++] << 8)

      sample := new Int16Array(
        new Uint8Array(
          data.subarray(
            @samplingData.offset + start * 2,
            @samplingData.offset + end * 2
          )
        ).buffer
      )

      startLoop -= start
      endLoop -= start

      samples.push(sample)

      sampleHeader.push({
        sampleName: sampleName,
        start: start,
        end: end,
        startLoop: startLoop,
        endLoop: endLoop,
        sampleRate: sampleRate,
        originalPitch: originalPitch,
        pitchCorrection: pitchCorrection,
        sampleLink: sampleLink,
        sampleType: sampleType,
      })

    { sampleHeader, samples } as const

  parseModulator(chunk: Chunk): ModGen[]
    data := @input
    ip .= chunk.offset
    size := chunk.offset + chunk.size

    output := []

    while ip < size
      // Src  Oper
      // TODO
      ip += 2

      // Dest Oper
      code := data[ip++] | (data[ip++] << 8)
      key := SF2Parser.GeneratorEnumeratorTable[code]
      if key is undefined
        // Amount
        output.push
          type: key
          value:
            code: code
            amount: data[ip] | (((data[ip + 1] << 8) << 16) >> 16)
            lo: data[ip++]
            hi: data[ip++]

      else
        // Amount
        switch key
          case "keyRange": /* FALLTHROUGH */
          case "velRange": /* FALLTHROUGH */
          case "keynum": /* FALLTHROUGH */
          case "velocity":
            output.push
              type: key
              value:
                lo: data[ip++]
                hi: data[ip++]

            break
          default:
            output.push
              type: key
              value:
                amount: data[ip++] | (((data[ip++] << 8) << 16) >> 16)

            break

      // AmtSrcOper
      // TODO
      ip += 2

      // Trans Oper
      // TODO
      ip += 2

    return output

  parseGenerator(chunk: Chunk): ModGen[]
    data := @input
    ip .= chunk.offset
    size := chunk.offset + chunk.size

    output := []

    while ip < size
      code := data[ip++] | (data[ip++] << 8)
      key := SF2Parser.GeneratorEnumeratorTable[code]
      if key is undefined
        output.push
          type: key
          value:
            code: code
            amount: data[ip] | (((data[ip + 1] << 8) << 16) >> 16)
            lo: data[ip++]
            hi: data[ip++]
        continue

      switch key
        case "keynum": /* FALLTHROUGH */
        case "keyRange": /* FALLTHROUGH */
        case "velRange": /* FALLTHROUGH */
        case "velocity":
          output.push
            type: key
            value:
              lo: data[ip++]
              hi: data[ip++]
          break
        default:
          output.push
            type: key
            value:
              amount: data[ip++] | (((data[ip++] << 8) << 16) >> 16)
          break

    return output

  getInstruments()
    var instrument = @instrument
    var zone = @instrumentZone

    var output = []

    // instrument -> instrument bag -> generator / modulator
    for (let i = 0, il = instrument.length; i < il; ++i)
      bagIndex := instrument[i].instrumentBagIndex
      bagIndexEnd := instrument[i + 1]
        ? instrument[i + 1].instrumentBagIndex
        : zone.length
      zoneInfo := []

      // instrument bag
      for (let j = bagIndex, jl = bagIndexEnd; j < jl; ++j)
        instrumentGenerator := @createInstrumentGenerator_(zone, j)
        instrumentModulator := @createInstrumentModulator_(zone, j)

        zoneInfo.push
          generator: instrumentGenerator.generator,
          generatorSequence: instrumentGenerator.generatorInfo,
          modulator: instrumentModulator.modulator,
          modulatorSequence: instrumentModulator.modulatorInfo,

      output.push
        name: instrument[i].instrumentName,
        info: zoneInfo,

    return output

  getPresets()
    preset := @presetHeader
    zone := @presetZone
    output := []

    // preset -> preset bag -> generator / modulator
    for (let i = 0, il = preset.length; i < il; ++i)
      bagIndex := preset[i].presetBagIndex
      bagIndexEnd := preset[i + 1] ? preset[i + 1].presetBagIndex : zone.length
      zoneInfo := []

      let instrument
      // preset bag
      for (let j = bagIndex, jl = bagIndexEnd; j < jl; ++j)
        presetGenerator := @createPresetGenerator_(zone, j)
        presetModulator := @createPresetModulator_(zone, j)

        zoneInfo.push
          generator: presetGenerator.generator
          generatorSequence: presetGenerator.generatorInfo
          modulator: presetModulator.modulator
          modulatorSequence: presetModulator.modulatorInfo

        // TODO: does this get overwritten during the loop or is instrument always last?
        instrument =
          presetGenerator.generator.instrument !== undefined
            ? presetGenerator.generator.instrument.amount
            : presetModulator.modulator.instrument !== undefined
            ? presetModulator.modulator.instrument.amount
            : null

      output.push
        name: preset[i].presetName
        info: zoneInfo
        header: preset[i]
        instrument: instrument

    return output

  createInstrumentGenerator_(zone: SF2Parser["instrumentZone"], index: number)
    modgen := @createBagModGen_(
      zone,
      zone[index].instrumentGeneratorIndex,
      zone[index + 1]
        ? zone[index + 1].instrumentGeneratorIndex
        : @instrumentZoneGenerator.length,
      @instrumentZoneGenerator
    )

    generator: modgen.modgen
    generatorInfo: modgen.modgenInfo

  createInstrumentModulator_(zone: SF2Parser["instrumentZone"], index: number)
    var modgen = @createBagModGen_(
      zone,
      zone[index].instrumentModulatorIndex,
      zone[index + 1]
        ? zone[index + 1].instrumentModulatorIndex
        : @instrumentZoneModulator.length,
      @instrumentZoneModulator
    )

    modulator: modgen.modgen,
    modulatorInfo: modgen.modgenInfo,

  createPresetGenerator_(zone: SF2Parser["presetZone"], index: number)
    modgen := @createBagModGen_(
      zone,
      zone[index].presetGeneratorIndex,
      zone[index + 1]
        ? zone[index + 1].presetGeneratorIndex
        : @presetZoneGenerator.length,
      @presetZoneGenerator
    )

    generator: modgen.modgen
    generatorInfo: modgen.modgenInfo

  createPresetModulator_(zone: SF2Parser["presetZone"], index: number)
    modgen := @createBagModGen_(
      zone,
      zone[index].presetModulatorIndex,
      zone[index + 1]
        ? zone[index + 1].presetModulatorIndex
        : @presetZoneModulator.length,
      @presetZoneModulator
    )

    modulator: modgen.modgen,
    modulatorInfo: modgen.modgenInfo,

  createBagModGen_(
    zone: unknown,
    indexStart: number,
    indexEnd: number,
    zoneModGen: ModGen[]
  )
    modgenInfo := []
    // @ts-ignore
    modgen: BagModGen := {
      unknown: [] as ModGen['value'][],
      keyRange: {
        hi: 127,
        lo: 0,
      },
    } // TODO

    for (let i = indexStart, il = indexEnd; i < il; ++i)
      info := zoneModGen[i]
      modgenInfo.push(info)

      if info.type is undefined
        modgen.unknown.push(info.value)
      else
        modgen[info.type] = info.value


    { modgen, modgenInfo }

  static GeneratorEnumeratorTable = [
    "startAddrsOffset",
    "endAddrsOffset",
    "startloopAddrsOffset",
    "endloopAddrsOffset",
    "startAddrsCoarseOffset",
    "modLfoToPitch",
    "vibLfoToPitch",
    "modEnvToPitch",
    "initialFilterFc",
    "initialFilterQ",
    "modLfoToFilterFc",
    "modEnvToFilterFc",
    "endAddrsCoarseOffset",
    "modLfoToVolume",
    undefined, // 14
    "chorusEffectsSend",
    "reverbEffectsSend",
    "pan",
    undefined,
    undefined,
    undefined, // 18,19,20
    "delayModLFO",
    "freqModLFO",
    "delayVibLFO",
    "freqVibLFO",
    "delayModEnv",
    "attackModEnv",
    "holdModEnv",
    "decayModEnv",
    "sustainModEnv",
    "releaseModEnv",
    "keynumToModEnvHold",
    "keynumToModEnvDecay",
    "delayVolEnv",
    "attackVolEnv",
    "holdVolEnv",
    "decayVolEnv",
    "sustainVolEnv",
    "releaseVolEnv",
    "keynumToVolEnvHold",
    "keynumToVolEnvDecay",
    "instrument",
    undefined, // 42
    "keyRange",
    "velRange",
    "startloopAddrsCoarseOffset",
    "keynum",
    "velocity",
    "initialAttenuation",
    undefined, // 49
    "endloopAddrsCoarseOffset",
    "coarseTune",
    "fineTune",
    "sampleID",
    "sampleModes",
    undefined, // 55
    "scaleTuning",
    "exclusiveClass",
    "overridingRootKey",
  ] as const

class Chunk
  type: string
  size: number
  offset: number

  constructor(@type: string, @size: number, @offset: number)

  assertType(type: string)
    if @type !== type
      /* c8 ignore next */
      throw new Error("invalid chunk type:" + @type)

type RiffParserOptions =
  index?: number
  length?: number

class RiffParser
  input: Uint8Array
  ip: number
  length: number
  offset: number
  chunkList: Chunk[] = []

  constructor(@input: Uint8Array, options: RiffParserOptions = {})
    @ip = options.index || 0
    @length = options.length || @input.length - @ip
    @offset = @ip

  parse(): void
    length := @length + @offset

    while @ip < length
      @parseChunk()

  parseChunk(): void
    input := @input
    ip .= @ip
    let size

    @chunkList.push
      new Chunk
        String.fromCharCode(input[ip++], input[ip++], input[ip++], input[ip++])
        (size = (input[ip++] |
          (input[ip++] << 8) |
          (input[ip++] << 16) |
          (input[ip++] << 24)) >>>
        0)
        ip

    ip += size

    @ip = ip

  getChunk(index: number): Chunk?
    @chunkList[index]

  getNumberOfChunks()
    @chunkList.length
